import React, { useState, useEffect } from "react";
import {
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Input
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import api from "../../services/api";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";

const ClienteCustomFieldsForm = ({ values = {}, onChange, onDefinitionsLoaded }) => {
  const [definitions, setDefinitions] = useState([]);
  const [customFieldsValues, setCustomFieldsValues] = useState(values);

  useEffect(() => {
    loadDefinitions();
  }, []);

  useEffect(() => {
    setCustomFieldsValues(values);
  }, [values]);

  const loadDefinitions = async () => {
    try {
      const { data } = await api.get("/cliente-custom-field-definitions");
      
      // Garantir que sempre seja um array
      const definitions = Array.isArray(data?.definitions) 
        ? data.definitions 
        : Array.isArray(data) 
        ? data 
        : [];
      
      setDefinitions(definitions);
      if (onDefinitionsLoaded) {
        onDefinitionsLoaded(definitions);
      }

      // Inicializar valores padrão
      const defaultValues = {};
      definitions.forEach(def => {
        if (def.defaultValue && !values[def.fieldName]) {
          defaultValues[def.fieldName] = def.defaultValue;
        }
      });
      
      if (Object.keys(defaultValues).length > 0) {
        const newValues = { ...values, ...defaultValues };
        setCustomFieldsValues(newValues);
        if (onChange) {
          onChange(newValues);
        }
      }
    } catch (err) {
      console.error("Erro ao carregar campos personalizados:", err);
      setDefinitions([]); // Garantir que seja um array vazio em caso de erro
      if (onDefinitionsLoaded) {
        onDefinitionsLoaded([]);
      }
    }
  };

  const handleFieldChange = (fieldName, value) => {
    const newValues = {
      ...customFieldsValues,
      [fieldName]: value
    };
    setCustomFieldsValues(newValues);
    if (onChange) {
      onChange(newValues);
    }
  };

  const renderField = (definition) => {
    const value = customFieldsValues[definition.fieldName] || "";

    switch (definition.fieldType) {
      case "text":
      case "email":
      case "phone":
      case "cpf":
      case "cnpj":
      case "cep":
        return (
          <TextField
            fullWidth
            label={definition.fieldLabel}
            value={value}
            onChange={(e) => handleFieldChange(definition.fieldName, e.target.value)}
            required={definition.required}
            type={definition.fieldType === "email" ? "email" : "text"}
          />
        );

      case "number":
        return (
          <TextField
            fullWidth
            label={definition.fieldLabel}
            value={value}
            onChange={(e) => handleFieldChange(definition.fieldName, e.target.value)}
            required={definition.required}
            type="number"
          />
        );

      case "date":
        return (
          <TextField
            fullWidth
            label={definition.fieldLabel}
            value={value}
            onChange={(e) => handleFieldChange(definition.fieldName, e.target.value)}
            required={definition.required}
            type="date"
            InputLabelProps={{
              shrink: true,
            }}
          />
        );

      case "textarea":
        return (
          <TextField
            fullWidth
            label={definition.fieldLabel}
            value={value}
            onChange={(e) => handleFieldChange(definition.fieldName, e.target.value)}
            required={definition.required}
            multiline
            rows={4}
          />
        );

      case "select":
        return (
          <TextField
            fullWidth
            select
            label={definition.fieldLabel}
            value={value}
            onChange={(e) => handleFieldChange(definition.fieldName, e.target.value)}
            required={definition.required}
          >
            <MenuItem value="">
              <em>{i18n.t("clienteCustomFieldsForm.selectPlaceholder")}</em>
            </MenuItem>
            {definition.fieldOptions && definition.fieldOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        );

      case "multiselect":
        return (
          <FormControl fullWidth>
            <InputLabel>{definition.fieldLabel}</InputLabel>
            <Select
              multiple
              value={Array.isArray(value) ? value : (value ? [value] : [])}
              onChange={(e) => handleFieldChange(definition.fieldName, e.target.value)}
              input={<Input />}
              renderValue={(selected) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {selected.map((val) => (
                    <Chip key={val} label={val} size="small" />
                  ))}
                </div>
              )}
            >
              {definition.fieldOptions && definition.fieldOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case "checkbox":
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={value === true || value === "true" || value === "1"}
                onChange={(e) => handleFieldChange(definition.fieldName, e.target.checked)}
                color="primary"
              />
            }
            label={definition.fieldLabel}
          />
        );

      default:
        return (
          <TextField
            fullWidth
            label={definition.fieldLabel}
            value={value}
            onChange={(e) => handleFieldChange(definition.fieldName, e.target.value)}
            required={definition.required}
          />
        );
    }
  };

  if (definitions.length === 0) {
    return null;
  }

  return (
    <Grid container spacing={2}>
      {definitions.map((definition) => (
        <Grid item xs={12} sm={6} key={definition.id}>
          {renderField(definition)}
        </Grid>
      ))}
    </Grid>
  );
};

export default ClienteCustomFieldsForm;
